import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';

import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { Employee, EmployeeCreate, EmployeeFilter, EmployeeUpdate } from '@core/types/employee.type';

@Injectable({ providedIn: 'root' })
export class EmployeeService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/employees`;

  grid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<Employee>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) {
      params = params.set('searchTerm', config.searchTerm);
    }

    const filter = (config.filter || {}) as EmployeeFilter;

    if (filter.isActive !== undefined) {
      params = params.set('filter.isActive', String(filter.isActive));
    }

    if (filter.employeeType !== undefined) {
      params = params.set('filter.employeeType', String(filter.employeeType));
    }

    if (filter.position) {
      params = params.set('filter.position', filter.position);
    }

    return this.http.get<ApiResponse<PaginationResult<Employee>>>(`${this.baseUrl}/grid`, { params }).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message || 'Error loading employees');
      }),
    );
  }

  get(id: string): Observable<ApiResponse<Employee>> {
    return this.http.get<ApiResponse<Employee>>(`${this.baseUrl}/${id}`).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message || 'Error loading employee');
      }),
    );
  }

  create(data: EmployeeCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.baseUrl, data).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message || 'Error creating employee');
      }),
    );
  }

  update(data: EmployeeUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.baseUrl, data).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message || 'Error updating employee');
      }),
    );
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message || 'Error deleting employee');
      }),
    );
  }
}

